import { type ReactNode, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  FormControl,
  IconButton,
  OutlinedInput,
  Paper,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Form } from '@realadvisor/form';
import { X } from 'lucide-react';
import normalizeUrl from 'normalize-url';
import type Quill from 'quill';

import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { useLocale } from '@shared/hooks/locale';

import { isSpecialLink } from './linkUtils';

export type ComposerLink = { url: string; title: string };

export type ComposerToolbarState = {
  isBold: boolean;
  isItalic: boolean;
  blockType: 'ordered' | 'bullet' | 'paragraph';
  link: null | ComposerLink;
  selectedText: string;
};

type ComposerLinkEditor = Pick<
  Quill,
  'deleteText' | 'formatText' | 'getSelection' | 'getText' | 'insertText'
>;

type ComposerSelection = { index: number; length: number };

type ComposerTextReader = Pick<Quill, 'getText'>;

const ALLOWED_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

const hasDisallowedScheme = (url: string): boolean => {
  try {
    return !ALLOWED_SCHEMES.has(new URL(url.trim()).protocol);
  } catch {
    return false;
  }
};

export const normalizeUrlOrSpecialLink = (url: string): string => {
  if (hasDisallowedScheme(url)) {
    return '';
  }
  if (isSpecialLink(url)) {
    return url.trim();
  }
  try {
    return normalizeUrl(url);
  } catch {
    return url.trim();
  }
};

export const readComposerSelectionText = (
  quill: ComposerTextReader,
  range: ComposerSelection,
): string => (range.length > 0 ? quill.getText(range.index, range.length) : '');

export const readComposerToolbarState = (
  quill: Pick<Quill, 'getSelection' | 'getFormat' | 'getText'>,
  transformLinkUrl: (url: string) => string = url => url,
): ComposerToolbarState => {
  const range = quill.getSelection();
  if (range == null) {
    return {
      isBold: false,
      isItalic: false,
      blockType: 'paragraph',
      link: null,
      selectedText: '',
    };
  }
  const format = quill.getFormat(range);
  const selectedText = readComposerSelectionText(quill, range);
  const linkUrl = format.link;
  let link: null | ComposerLink = null;
  if (typeof linkUrl === 'string') {
    link = {
      url: transformLinkUrl(linkUrl),
      title: selectedText,
    };
  }
  return {
    isBold: format.bold === true,
    isItalic: format.italic === true,
    blockType:
      format.list === 'ordered'
        ? 'ordered'
        : format.list === 'bullet'
        ? 'bullet'
        : 'paragraph',
    link,
    selectedText,
  };
};

export const applyComposerLink = (
  quill: ComposerLinkEditor,
  { url, title }: ComposerLink,
): void => {
  const range = quill.getSelection(true);
  if (range == null) {
    return;
  }
  if (range.length === 0) {
    quill.insertText(range.index, title, { link: url }, 'user');
    return;
  }
  const selectedText = readComposerSelectionText(quill, range);
  if (title && title !== selectedText) {
    quill.deleteText(range.index, range.length, 'user');
    quill.insertText(range.index, title, { link: url }, 'user');
    return;
  }
  quill.formatText(range.index, range.length, 'link', url, 'user');
};

export const ToolbarDialog = ({
  anchorEl,
  open,
  onClose,
  children,
}: {
  anchorEl: Element | null;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <Dialog
        disableRestoreFocus
        open={open}
        onClose={onClose}
        css={{
          '.MuiDialog-paper': {
            marginLeft: '18px !important',
            marginRight: '18px !important',
          },
        }}
      >
        {children}
      </Dialog>
    );
  }

  return (
    <Popover
      disableRestoreFocus
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Paper sx={{ overflow: 'auto', minWidth: 300 }}>{children}</Paper>
    </Popover>
  );
};

export const LinkDialogContent = ({
  link,
  title: initialTitle,
  onSave,
  onClose,
}: {
  link: null | ComposerLink;
  title: string;
  onSave: (link: ComposerLink) => void;
  onClose: () => void;
}) => {
  const { t } = useLocale();
  const [url, setUrl] = useState(link?.url ?? 'https://');
  const [title, setTitle] = useState(initialTitle);
  return (
    <Form
      onSubmit={() => {
        const normalizedUrl = normalizeUrlOrSpecialLink(url);
        if (!normalizedUrl) {
          onClose();
          return;
        }
        onSave({
          url: normalizedUrl,
          title: title || normalizedUrl,
        });
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>{t('insertLink')}</Typography>
          <IconButton size="small" onClick={onClose} sx={{ mr: -0.5 }}>
            <X size={18} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControl fullWidth>
            <RaLabel label={t('Title')} />
            <OutlinedInput
              size="small"
              value={title}
              onChange={event => setTitle(event.target.value)}
              sx={{ background: 'white' }}
            />
          </FormControl>
          <FormControl fullWidth>
            <RaLabel label={t('URL')} />
            <OutlinedInput
              size="small"
              autoFocus
              value={url}
              onChange={event => setUrl(event.target.value)}
              sx={{ background: 'white' }}
            />
          </FormControl>
        </Box>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}
        >
          <Button onClick={onClose} size="small">
            {t('Cancel')}
          </Button>
          <Button type="submit" variant="contained" size="small">
            {t('Save')}
          </Button>
        </Box>
      </Box>
    </Form>
  );
};
